import { useState } from 'react';

// Components
import TailwindTheme from '../../../../../packages/tailwind/tailwind-theme';
import { PillTabsV2 } from '@velinsi/smp-widgets-ui/src/molecules/PillTabsV2/PillTabsV2';
import { RankingTable } from '../../components/RankingTable';

// Hooks
import { useAvailableRankings } from '../../hooks/useAvailableRankings';
import { useRanking } from '../../hooks/useRanking';
import useRootNodeRef from '../../../../../packages/hooks/useRootNodeRef';

// Utils
import { ConfigContext } from '@velinsi/smp-widgets-ui';
import { defaultHandlePlayerClick, defaultHandleUserInteractionClick } from '@sportal365/smp-widget-utils';

// Types
import { BaseWidgetProps } from '../../../../../packages/types';
import { ITennisRankingConfig } from '../../../../../packages/types/tennisV2';

// Constants
import { USER_INTERACTION } from '../../../../../packages/constants/entityTypes';
import { TAB_SELECTION_SUBSCRIBE_TYPE } from '../../../../../packages/constants/subscribeTypes';
import { DEFAULT_LIMIT, DEFAULT_OFFSET } from '../../constants/common';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import tailwindStyles from '../../styles.css';
import { IThemeCustomization, Themes } from '../../../../../packages/tailwind/types';

export type TennisWidgetsProps = BaseWidgetProps<ITennisRankingConfig>;

export const RankingWidget = ({ themes, config, subscribers, sdkOptions, shadow, cuid }: TennisWidgetsProps) => {
  const initialLimit = config?.dataLimit ? Number(config?.dataLimit) : DEFAULT_LIMIT;
  const [limit, setLimit] = useState<number>(initialLimit);

  const { ref } = useRootNodeRef();

  const {
    availableRankings,
    selectedRanking,
    pillTabOptions,
    setSelectedRanking,
    isLoading: isAvailableRankingsLoading,
  } = useAvailableRankings({
    ...config,
    dataWidgetCuid: cuid,
    subscribers,
    sdkOptions,
  });

  const {
    ranking,
    handleClick,
    isLoading: isRankingLoading,
  } = useRanking({
    ...config,
    dataHighlightEntities: config?.dataHighlightEntities ?? [],
    rankingId: selectedRanking?.id ?? '',
    limit: limit,
    offset: config?.dataOffset ? Number(config?.dataOffset) : DEFAULT_OFFSET,
    countryId: config?.dataCountryId ?? '',
    dataWidgetCuid: cuid,
    subscribers,
    sdkOptions,
  });

  const handleRankingSelect = (value: string) => {
    const selected = availableRankings?.find((ranking) => ranking.id === value);
    if (!selected) return;
    setSelectedRanking({ id: selected.id, label: selected.standing_type?.name || '' });
    defaultHandleUserInteractionClick(handleClick, USER_INTERACTION, TAB_SELECTION_SUBSCRIBE_TYPE);
  };

  const handleParticipantClick = (id: string | number, name: string) => defaultHandlePlayerClick(handleClick, id, name);

  return (
    <ConfigContext.Provider value={{ ...config, sdkOptions }}>
      <TailwindTheme
        isLoading={isAvailableRankingsLoading || isRankingLoading}
        shadow={shadow}
        themes={themes ? (themes as IThemeCustomization) : undefined}
        dataTheme={config?.dataTheme ? (config.dataTheme as Themes) : undefined}
        refForShadow={ref}
        tailwindStyles={tailwindStyles}
        bgColor='var(--colors-primaryBackgroundColor)'
      >
        {config?.dataOptionsDisplay && (
          <div className='py-3 px-4 bg-rowBackgroundColor'>
            <PillTabsV2
              selectedPillTab={selectedRanking.id}
              pillTabOptions={pillTabOptions}
              handlePillTabChange={(value: string) => handleRankingSelect(value)}
            />
          </div>
        )}
        {ranking && (
          <RankingTable
            ranking={ranking}
            limit={limit}
            initialLimit={initialLimit}
            maxLimit={ranking.page_meta?.total || 0}
            setLimit={setLimit}
            handleParticipantClick={handleParticipantClick}
            handleSubscriptionClick={handleClick}
          />
        )}
      </TailwindTheme>
    </ConfigContext.Provider>
  );
};
